/**
 * @file DivisionDropdown.js
 * @description A dropdown component for selecting a division.
 */

import React, { useState, useEffect } from 'react';
import { fetchDivisions } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const DivisionDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedDivision,
    onDivisionChange,
    isPublic = false,
    companyCode = null,
    required = false,
    errorMessage = null,
}) => {
    const [divisions, setDivisions] = useState([]);
    const [defaultValue, setDefaultValue] = useState('Select Division');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getDivisions = async () => {
            try {
                const data = await fetchDivisions(tenantId, {
                    isPublic,
                    companyCode,
                });
                setDivisions(data);

                if (selectedDivision) {
                    const matchingDivision = data.find(
                        division => division.id === selectedDivision,
                    );
                    setDefaultValue(
                        matchingDivision
                            ? matchingDivision.name
                            : 'Select Division',
                    );
                }
            } catch (error) {
                console.error('Could not fetch divisions:', error);
                Toast.error(getText('msg_fd03cb9f9b68'));
            } finally {
                setIsLoading(false);
            }
        };

        getDivisions();
    }, [tenantId, selectedDivision, isPublic, companyCode]);

    const divisionOptions = [
        { value: null, label: getText('msg_fb1784435e3b') },
        ...divisions.map(format => ({
            value: format.id,
            label: format.name,
        })),
    ];

    useEffect(() => {
        if (divisions.length > 0 && selectedDivision) {
            const selectedDivisionObj = divisions.find(
                division => division.id === selectedDivision,
            );
            setDefaultValue(
                selectedDivisionObj
                    ? selectedDivisionObj.name
                    : 'Select Division',
            );
        }
    }, [selectedDivision, divisions]);

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="division">
                {getText('msg_bbbec4441626')}
                {required && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    defaultValue={defaultValue}
                    options={divisionOptions}
                    value={selectedDivision}
                    onChange={onDivisionChange}
                    disabled={divisions.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default DivisionDropdown;
